import React, { useEffect, useState, useRef } from "react";
import Table from "components/Tables/Table";
import Row from "components/Tables/Row";
import Cell from "components/Tables/Cell";
import Column from "components/Tables/Column";
import Pill from "components/Text/Pill";
import { Link } from "react-router-dom";
import { LockClosedIcon } from "@heroicons/react/outline";
import { fromSqlDateTime } from "helpers/Date";
import { packClasses } from "Utils";
import { background as statusColors } from "constants/status-colors";
import EmptyState from "./EmptyState";

export default function ContentTable(props) {
    const itemRef = useRef(null);

    const [selectedItems, setSelectedItems] = useState(
        props.selectedItems ? props.selectedItems : []
    );

    const itemCount = props.items.length ? props.items.length : 0;

    useEffect(() => {
        setSelectedItems(props?.selectedItems || []);
    }, [props.selectedItems]);

    useEffect(() => {
        if (selectedItems.length && selectedItems.length !== itemCount) {
            itemRef.current.indeterminate = true;
            itemRef.current.checked = false;
        } else if (selectedItems.length && selectedItems.length === itemCount) {
            itemRef.current.indeterminate = false;
            itemRef.current.checked = true;
        } else {
            itemRef.current.indeterminate = false;
            itemRef.current.checked = false;
        }
    }, [selectedItems, itemCount]);

    const bubbleClickAction = (item, checked) => {
        if (checked) {
            setSelectedItems([...selectedItems, item.uuid]);
        } else {
            setSelectedItems(selectedItems.filter((i) => i !== item.uuid));
        }
        if (props.onSelect) {
            return props.onSelect.call(null, item, checked);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedItems(props.items.map((item) => item.uuid));
            if (props.onSelectAll) {
                props.onSelectAll.call(null, props.items);
            }
        } else {
            setSelectedItems([]);
            if (props.onSelectAll) {
                props.onSelectAll.call(null, []);
            }
        }
    };

    const cols = [
        <Column className="cursor-pointer text-left text-xs" key="ct-status">
            <span className="sr-only">Status</span>
        </Column>,
        <Column
            key="ct-checkbox"
            className={packClasses(!props.onSelect ? "hidden" : "")}
        >
            <input
                type="checkbox"
                className="appearance-none rounded text-green-700 focus:ring-green-700"
                ref={itemRef}
                onClick={handleSelectAll}
            />
        </Column>,
        <Column
            key="ct-title"
            className={packClasses(
                props.sortedColumn === "title"
                    ? "font-extrabold"
                    : "font-medium",
                "w-1/2 cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            )}
            onClick={
                props.onSortClick ? props.onSortClick.bind(null, "title") : null
            }
        >
            Title
        </Column>,
        <Column
            className={packClasses(
                props.sortedColumn === "modified_at"
                    ? "font-extrabold"
                    : "font-medium",
                "w-1/5 cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            )}
            key="ct-modtime"
            onClick={
                props.onSortClick
                    ? props.onSortClick.bind(null, "modified_at")
                    : null
            }
        >
            Modified
        </Column>,
        <Column
            key="ct-pubtime"
            className={packClasses(
                props.sortedColumn === "published_at"
                    ? "font-extrabold"
                    : "font-medium",
                "w-1/5 cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            )}
            onClick={
                props.onSortClick
                    ? props.onSortClick.bind(null, "published_at")
                    : null
            }
        >
            Published
        </Column>,
    ];

    const headerButtons = [
        <Column className="cursor-pointer text-left text-xs" key="ct-status">
            <span className="sr-only">Status</span>
        </Column>,
        <Column
            key="ct-checkbox"
            className={packClasses(!props.onSelect ? "hidden" : "")}
        >
            <input
                type="checkbox"
                className="appearance-none rounded text-green-700 focus:ring-green-700"
                ref={itemRef}
                onClick={handleSelectAll}
            />
        </Column>,
        <Column
            key="ct-buttons"
            colSpan={cols.length - 2}
            className={packClasses(
                "font-medium",
                "w-full cursor-pointer px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            )}
        >
            {props.headerButtons || null}
        </Column>,
    ];

    if (props.loading === true) {
        const loadingRows = [];
        for (let i = 0; i < 10; i++) {
            loadingRows.push(
                <Row className="animate-pulse" key={i}>
                    <Cell className="whitespace-nowrap px-4 py-2">
                        <span className="mx-0 mt-3 inline-block h-3 w-3 rounded-full bg-gray-300 leading-none">
                            &nbsp;
                        </span>
                    </Cell>
                    <Cell
                        className={packClasses(!props.onSelect ? "hidden" : "")}
                    >
                        <div className="h-4 w-4 rounded bg-gray-200">
                            &nbsp;
                        </div>
                    </Cell>
                    <Cell className="max-w-lg whitespace-nowrap px-4 py-2">
                        <div className="h-5 w-full rounded bg-gray-200">
                            &nbsp;
                        </div>
                        <div className="mt-2 h-5 w-1/3 rounded bg-gray-200">
                            &nbsp;
                        </div>
                    </Cell>
                    <Cell className="whitespace-nowrap px-6 py-2 text-xs text-gray-600">
                        <div className="h-5 w-full rounded bg-gray-200">
                            &nbsp;
                        </div>
                    </Cell>
                    <Cell className="whitespace-nowrap px-6 py-2 text-xs text-gray-600">
                        <div className="h-5 w-full rounded bg-gray-200">
                            &nbsp;
                        </div>
                    </Cell>
                </Row>
            );
        }
        return <Table columns={cols}>{loadingRows}</Table>;
    }

    if (!props.items || !props.items.length) {
        return (
            <Table columns={cols}>
                <Row>
                    <Cell colSpan={5}>
                        <EmptyState />
                    </Cell>
                </Row>
            </Table>
        );
    }
    return (
        <Table
            columns={
                props.headerButtons && selectedItems.length
                    ? headerButtons
                    : cols
            }
        >
            {props.items.map((item) => (
                <ContentTableRow
                    key={item.uuid}
                    item={item}
                    showSelect={props.onSelect ? true : false}
                    onSelect={bubbleClickAction}
                    selected={
                        selectedItems
                            ? selectedItems.indexOf(item.uuid) > -1
                            : false
                    }
                />
            ))}
        </Table>
    );
}

export function ContentTableRow(props) {
    const [selected, setSelected] = useState(props.selected ? true : false);

    useEffect(() => {
        setSelected(props.selected);
    }, [props.selected]);

    // const statusColors = {
    //     published: "bg-green-600",
    //     pending: "bg-blue-500",
    //     draft: "bg-gray-300",
    // };

    const handleClick = (e) => {
        if (props.onSelect) {
            props.onSelect.call(null, props.item, e.target.checked);
        }
        setSelected(e.target.checked);
    };

    return (
        <Row className={selected ? "bg-green-50" : ""}>
            <Cell className="whitespace-nowrap px-4 py-2">
                <span
                    className={packClasses(
                        "mx-0 mt-3 inline-block h-3 w-3 rounded-full leading-none",
                        statusColors[props.item.status]
                    )}
                >
                    &nbsp;
                </span>
            </Cell>
            <Cell className={packClasses(!props.showSelect ? "hidden" : "")}>
                <input
                    type="checkbox"
                    className="my-0 appearance-none rounded leading-none text-green-700 focus:ring-green-700"
                    onChange={handleClick}
                    checked={selected}
                />
            </Cell>
            <Cell className="max-w-lg whitespace-nowrap px-4 py-2">
                <div className="flex items-center space-x-2">
                    <div className="flex-grow overflow-hidden">
                        <Link
                            to={props.item.edit_url}
                            className="color-gray-400 block truncate text-sm hover:underline"
                        >
                            {props.item.title ? (
                                props.item.title
                            ) : (
                                <span className="italic text-gray-600">
                                    Untitled
                                </span>
                            )}
                        </Link>
                        <Pill
                            className={packClasses(
                                "border border-transparent bg-gray-100 text-gray-700",
                                selected ? "border-gray-200" : ""
                            )}
                        >
                            {props.item.type === "media"
                                ? props.item?.attachment?.type
                                : props.item?.type}
                        </Pill>{" "}
                        <Pill
                            className={packClasses(
                                "border border-transparent bg-gray-100 text-gray-700",
                                selected ? "border-gray-200" : ""
                            )}
                        >
                            {props.item.workflow && props.item.workflow.uuid ? (
                                props.item.workflow.name
                            ) : (
                                <span className="text-gray-300">None</span>
                            )}
                        </Pill>{" "}
                        {props.item.lock && props.item.lock.user && (
                            <Pill
                                className={packClasses(
                                    "border border-transparent bg-gray-100 text-red-700",
                                    selected ? "border-gray-200" : ""
                                )}
                            >
                                <LockClosedIcon className=" mr-1 h-2.5 w-2.5" />{" "}
                                {props.item.lock.user.name}
                            </Pill>
                        )}
                    </div>
                    {props.item?.attachment?.uuid && (
                        <img
                            src={
                                props.item.attachment.imgix_url +
                                "?w=256&ar=4:3&fit=crop&crop=faces&dpr=2"
                            }
                            alt={props.item.attachment.uuid}
                            style={{ width: "6rem", minWidth: "6rem" }}
                            className="w-64 overflow-hidden"
                        />
                    )}
                </div>
            </Cell>
            <Cell className="whitespace-nowrap px-6 py-2 text-xs text-gray-600">
                {fromSqlDateTime(props.item.modified_at).toFormat("ff")}
            </Cell>
            <Cell className="whitespace-nowrap px-6 py-2 text-xs text-gray-600">
                {props.item.published_at
                    ? fromSqlDateTime(props.item.published_at).toFormat("ff")
                    : "Draft"}
            </Cell>
        </Row>
    );
}
